import {
  createMachine, assign, fromPromise, assertEvent,
} from 'xstate';
import {
  BillingInterval, CurrentPlan, PaymentMethod, Plan,
} from '../types';
import paymentDetailsMachine from '../../stripe/payment-details/service/payment-details-machine';

export interface PlanSelectContext {
  currentPlan: CurrentPlan;
  plan?: Plan;
  plans: Plan[];
  billingInterval?: BillingInterval;
  defaultPaymentMethod: PaymentMethod | null;
  paymentDetailsRef?: any;
  priceCheck?: any;
  toastMessage: string;
  toastSeverity: string;
  errorMessage: string;
  minConversionLicenses: number;
}

type Events =
  | { type: 'SELECT_PLAN'; plan: Plan; billingInterval: BillingInterval }
  | { type: 'CANCEL' }
  | { type: 'PAYMENT_DETAILS_DONE' }
  | { type: 'CLOSE_TOAST' }
  | { type: 'CONFIRM_PAYMENT' }
  | { type: 'NOTIFY_ERROR'; error: Error };

const planSelectMachine = createMachine(
  {
    id: 'plan-select',
    types: {} as {
      context: PlanSelectContext;
      events: Events;
      input: PlanSelectContext;
    },
    context: ({ input }) => ({
      currentPlan: input.currentPlan,
      plans: input.plans,
      defaultPaymentMethod: input.defaultPaymentMethod,
      toastMessage: input.toastMessage || '',
      toastSeverity: input.toastSeverity || '',
      errorMessage: input.errorMessage || '',
      plan: input.plan,
      billingInterval: input.billingInterval,
      paymentDetailsRef: input.paymentDetailsRef,
      priceCheck: input.priceCheck,
      minConversionLicenses: input.minConversionLicenses,
    }),
    initial: 'selectingPlan',
    on: {
      CANCEL: {
        target: '#plan-select.selectingPlan',
        actions: [
          'deselectPlan',
          'clearErrorMessage',
        ],
      },
      CLOSE_TOAST: {
        actions: 'closeToast',
      },
    },
    states: {
      selectingPlan: {
        on: {
          SELECT_PLAN: [
            {
              guard: 'hasDefaultPaymentMethod',
              target: 'gettingPriceCheck',
              actions: 'assignPlanAndBillingInterval',
            },
            {
              target: 'gettingPaymentDetails',
              actions: [
                'assignPlanAndBillingInterval',
                'spawnPaymentDetails',
              ],
            },
          ],
        },
      },
      gettingPaymentDetails: {
        on: {
          PAYMENT_DETAILS_DONE: {
            target: 'gettingPriceCheck',
            actions: 'clearErrorMessage',
          },
          NOTIFY_ERROR: {
            actions: 'handleErrorNotification',
          },
        },
      },
      gettingPriceCheck: {
        invoke: {
          src: 'fetchPriceCheck',
          input: ({ context }) => context,
          onDone: {
            target: 'confirmingPayment',
            actions: [
              assign({
                priceCheck: ({ event }) => event.output,
              }),
              'clearErrorMessage',
            ],
          },
          onError: {
            actions: assign({
              errorMessage: () => 'Error getting price check',
            }),
          },
        },
      },
      confirmingPayment: {
        on: {
          CONFIRM_PAYMENT: {
            guard: 'hasPlanAndBillingInterval',
            target: 'submittingPlanSelect',
          },
        },
      },
      submittingPlanSelect: {
        invoke: {
          src: 'submitPlanSelect',
          input: ({ context }) => context,
          onDone: {
            target: '#plan-select.selectingPlan',
            actions: [
              'updateCurrentPlan',
              'deselectPlan',
              'showSuccessToast',
              'clearErrorMessage',
            ],
          },
          onError: {
            target: 'confirmingPayment',
            actions: assign({
              errorMessage: ({ event }) => (event.error instanceof Error ? event.error.message : String(event.error)) || 'Error submitting plan update',
            }),
          },
        },
        on: {
          CANCEL: undefined,
        },
      },
    },
  },
  {
    actions: {
      assignPlanAndBillingInterval: assign({
        plan: ({ event }) => {
          assertEvent(event, 'SELECT_PLAN');
          return event.plan;
        },
        billingInterval: ({ event }) => {
          assertEvent(event, 'SELECT_PLAN');
          return event.billingInterval;
        },
      }),
      deselectPlan: assign({
        plan: () => undefined,
        billingInterval: () => undefined,
        priceCheck: () => undefined,
      }),
      updateCurrentPlan: assign({
        currentPlan: ({ context }): CurrentPlan => ({
          name: context.plan?.name as string,
          description: context.plan?.description as string,
          publicSlug: context.plan?.publicSlug as string,
          billingInterval: context.billingInterval as BillingInterval,
          upgradeRank: context.plan?.upgradeRank as number,
          usage: context.currentPlan?.usage,
        }),
      }),
      handleErrorNotification: assign({
        errorMessage: ({ event }) => {
          assertEvent(event, 'NOTIFY_ERROR');
          return event.error.message;
        },
      }),
      clearErrorMessage: assign({
        errorMessage: () => '',
      }),
      spawnPaymentDetails: assign({
        paymentDetailsRef: ({ spawn }) => spawn(paymentDetailsMachine, { id: 'payment-details' }),
      }),
      showSuccessToast: assign({
        toastMessage: () => 'Plan successfully updated',
        toastSeverity: () => 'success',
      }),
      closeToast: assign({ toastMessage: () => '' }),
    },
    guards: {
      hasDefaultPaymentMethod: ({ context }) => !!context.defaultPaymentMethod,
      hasPlanAndBillingInterval: ({ context }) => !!context.plan && !!context.billingInterval,
    },
    actors: {
      fetchPriceCheck: fromPromise(async ({ input }: { input: PlanSelectContext }) => {
        const ctx = input;
        if (!ctx.plan || !ctx.billingInterval) throw new Error('plan and billing interval needed');
        const params = new URLSearchParams({
          plan_slug: ctx.plan.publicSlug,
          billing_interval: ctx.billingInterval,
        });
        const url = `/settings/billing/plans/price_check?${params}`;
        const res = await fetch(url, {
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        return data;
      }),
      submitPlanSelect: fromPromise(async ({ input }: { input: PlanSelectContext }) => {
        const ctx = input;
        if (!ctx.plan || !ctx.billingInterval) throw new Error('plan and billing interval needed');
        const res = await fetch('/api/_private/plans/change_plan.json', {
          method: 'put',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plan_slug: ctx.plan.publicSlug,
            billing_interval: ctx.billingInterval,
          }),
        });
        if (!res.ok) {
          let message = 'Error submitting plan update';
          if (res.status === 409) {
            message = await res.text();
          }
          throw Error(message);
        }
      }),
    },
  },
);

export default planSelectMachine;
